/**
 * Toast notification primitive — shadcn-style, utilitarian (D-03).
 * Simple stack-based toast system.
 */
import * as React from 'react';

export type ToastVariant = 'default' | 'success' | 'warning' | 'danger';

export interface ToastMessage {
  id: string;
  message: string;
  variant?: ToastVariant;
}

interface ToastContextValue {
  addToast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast called outside <ToastProvider>');
  return ctx;
}

interface ToastProviderProps {
  children?: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<ToastMessage[]>([]);

  const addToast = React.useCallback(
    (message: string, variant: ToastVariant = 'default') => {
      const id = Math.random().toString(36).slice(2);
      setToasts((prev) => [...prev, { id, message, variant }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 4000);
    },
    [],
  );

  const variantClass: Record<ToastVariant, string> = {
    default: 'bg-bg-card border-border text-text',
    success: 'bg-success/20 border-success/40 text-success',
    warning: 'bg-warning/20 border-warning/40 text-warning',
    danger: 'bg-danger/20 border-danger/40 text-danger',
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {/* Toast stack */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-xs w-full">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={[
              'border rounded px-3 py-2 text-sm shadow-lg animate-in fade-in slide-in-from-right-2',
              variantClass[t.variant ?? 'default'],
            ].join(' ')}
            role="alert"
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
